import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useResources } from "../../hooks/useResources";
import { useToast } from "../../contexts/ToastContext";
import type {
  JobQuestion,
  QuestionStatus,
  StatusCardData,
} from "../../interfaces/componentInterfaces/jobDetails";
import { useJobDetailsStore } from "../../stores/jobDetails.store";
import { convertToCSV, downloadCSV } from "../../helpers/formatters";
import { fetchFileQuestionErrors } from "../../services/jobs.service";
import { formatDateTime } from "../table/TableUtils";
import DeleteModal from "../modal/DeleteModal";
import "./JobDetails.scss";
import JobDetailsHeader from "./JobDetailsHeader";
import JobDetailsInfo from "./JobDetailsInfo";
import JobQuestionsTable from "./JobQuestionsTable";
import JobHistory from "./JobHistory";

const JobDetails = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const { job, isLoading, error, load, reset, downloadPackage, deleteQuestion, fetchQuestionErrors } = useJobDetailsStore();

  const { getResourceValue } = useResources();
  const { showSuccess, showError, showInfo } = useToast();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);


  useEffect(() => {
    if (jobId) {
      load(jobId);
    }
    return () => reset();
  }, [jobId, load, reset]);

  if (isLoading) {
    return (
      <div className="job-details-loading">
        {/* {getResourceValue("loading_job_details", "Loading job details...")} */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-details-error">
        {getResourceValue("error_prefix", "Error:")} {error}
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-details-not-found">
        {getResourceValue("job_not_found", "Job not found")}
      </div>
    );
  }

  // Map API data to component structure
  const questions = job.job_status_payload?.questions || [];

  const succeededCount = questions.filter((q) => q.status === "passed").length;
  const pendingGraphicsCount = questions.filter(
    (q) => q.status === "pending_graphics"
  ).length;
  const publishedCount = questions.filter(
    (q) => q.status === "published"
  ).length;
  const failedCount = questions.filter((q) => q.status === "failed").length;

  const statusCards: StatusCardData[] = [
    {
      title: getResourceValue("succeeded_questions", "Succeeded Questions"),
      count: succeededCount,
      color: "green",
      type: "succeeded",
    },
    {
      title: getResourceValue("pending_graphics", "Pending Graphics"),
      count: pendingGraphicsCount,
      color: "orange",
      type: "pending",
    },
    {
      title: getResourceValue("published_questions", "Published Questions"),
      count: publishedCount,
      color: "blue",
      type: "published",
    },
    {
      title: getResourceValue("failed_questions", "Failed Questions"),
      count: failedCount,
      color: "red",
      type: "failed",
    },
  ];

  // Map API Question to Component JobQuestion
  const mappedQuestions: JobQuestion[] = questions.map((q) => {
    let status: QuestionStatus = "Uploaded"; // Default
    if (q.status === "passed") status = "Uploaded";
    if (q.status === "pending_graphics") status = "Pending Graphics";
    if (q.status === "failed") status = "Failed";
    if (q.status === "published") status = "Published";

    return {
      id: q.id,
      status: status,
    };
  });


  // Helper function to map API question status to filter type
  const mapApiStatusToFilter = (apiStatus: string): string | undefined => {
    switch (apiStatus) {
      case 'passed':
        return 'unpublished';
      case 'pending_graphics':
        return 'pending_graphics';
      case 'published':
        return 'published';
      default:
        return undefined;
    }
  };

  const handlePreview = (cardTypeOrQuestionId: string) => {
    if (!jobId) return;

    // Determine base URL based on job type
    const baseUrl = job.job_type === 'update' 
      ? `/questions/update/questions/${jobId}` 
      : `/questions/${jobId}`;

    // Check if it's a card type (from status cards) or questionId (from table)
    const isCardType = ['succeeded', 'pending', 'published', 'failed'].includes(cardTypeOrQuestionId);
    
    if (isCardType) {
      // Handle status card preview
      let filter: string | undefined;
      switch (cardTypeOrQuestionId) {
        case 'succeeded':
          filter = 'unpublished'; // Maps to "passed" status
          break;
        case 'pending':
          filter = 'pending_graphics';
          break;
        case 'published':
          filter = 'published';
          break;
        default:
          filter = undefined; // 'all' or 'failed' - show all
      }
      navigate(baseUrl, { 
        state: filter ? { filter } : undefined 
      });
    } else {
      // Handle question preview from table - need to find the question and its status
      const question = questions.find((q) => q.id === cardTypeOrQuestionId);
      if (question) {
        const filter = mapApiStatusToFilter(question.status);
        navigate(baseUrl, {
          state: {
            filter,
            questionId: cardTypeOrQuestionId,
          }
        });
      }
    }
  };

  const handleDeleteClick = (questionId: string) => {
    setQuestionToDelete(questionId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobId || !questionToDelete) return;

    setIsDeleting(true);
    try {
      await deleteQuestion(jobId, questionToDelete);
      // Reload job data to reflect the deletion
      await load(jobId);
      
      setIsDeleteModalOpen(false);
      setQuestionToDelete(null);
      
      // Show success toast
      showSuccess(
        getResourceValue("success", "Success"),
        getResourceValue("question_deleted_successfully", "Question deleted successfully")
      );
    } catch (error: any) {
      // Handle different error types
      let errorMessage = getResourceValue(
        "delete_question_failed",
        "Failed to delete question. Please try again."
      );

      if (error?.response?.status === 404) {
        const errorData = error.response?.data;
        if (errorData?.error_code === 40408) {
          errorMessage = getResourceValue(
            "question_not_found",
            "Question not found in job"
          );
        } else if (errorData?.error_code === 40407) {
          errorMessage = getResourceValue(
            "job_not_found",
            "Job not found"
          );
        }
      } else if (error?.response?.status === 400) {
        const errorData = error.response?.data;
        errorMessage = errorData?.message || errorMessage;
      } else if (error?.response?.status === 500) {
        errorMessage = getResourceValue(
          "server_error",
          "Server error occurred. Please try again later."
        );
      }

      showError(
        getResourceValue("error", "Error"),
        errorMessage
      );
      console.error("Error deleting question:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const triggerDownloadRedirect = async (status: any) => {
    if (!jobId) return;
    try {
      const { download_url } = await downloadPackage(jobId, status);
      if (download_url) {
        // Create a temporary anchor element to trigger download in current page
        const link = document.createElement('a');
        link.href = download_url;
        link.download = ''; // This suggests to download rather than navigate
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      showError(
        getResourceValue("error", "Error"),
        getResourceValue("download_failed", "Failed to start download. Please try again.")
      );
    }
  };

  const handleDownload = (cardType: string) => {
    let status: any;
    switch (cardType) {
      case 'succeeded': status = 'successed'; break;
      case 'pending': status = 'pending-graphics'; break;
      case 'published': status = 'published'; break;
      case 'failed': status = 'failed'; break;
      default: status = 'all';
    }
    void triggerDownloadRedirect(status);
  };

  const handleDownloadLog = async (_cardType: string) => {
    if (!jobId) return;

    try {
      // Fetch question errors from API
      const errors = await fetchQuestionErrors(jobId);

      // Convert to CSV format
      const csvContent = convertToCSV(errors as unknown as Record<string, unknown>[], ['question_id', 'status', 'message']);

      // Generate filename with job ID and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `job-${jobId}-errors-${timestamp}.csv`;

      // Download CSV file
      downloadCSV(csvContent, filename);

      // Show info message if no errors found
      if (errors.length === 0) {
        showInfo(
          getResourceValue("info", "Info"),
          getResourceValue("no_question_errors", "No question errors found for this job.")
        );
      }
      // Note: Success message is not shown to avoid interrupting the download
    } catch (error: any) {
      // Handle different error types
      let errorMessage = getResourceValue(
        "download_errors_failed",
        "Failed to download question errors. Please try again."
      );

      if (error?.response?.status === 404) {
        const errorData = error.response?.data;
        if (errorData?.error_code === 40407) {
          errorMessage = getResourceValue(
            "job_not_found",
            "Job not found"
          );
        }
      } else if (error?.response?.status === 400) {
        const errorData = error.response?.data;
        errorMessage = errorData?.message || errorMessage;
      } else if (error?.response?.status === 500) {
        errorMessage = getResourceValue(
          "server_error",
          "Server error occurred. Please try again later."
        );
      }

      showError(
        getResourceValue("error", "Error"),
        errorMessage
      );
      console.error("Error downloading question errors:", error);
    }
  };

  const handleDownloadZip = (cardType: string) => {
    handleDownload(cardType);
  };

  const handleDownloadAllZip = async () => {
    if (!jobId) return;
    try {
      const { download_url } = await downloadPackage(jobId, 'all');
      if (download_url) {
        const link = document.createElement('a');
        link.href = download_url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      showError(
        getResourceValue("error", "Error"),
        getResourceValue("download_failed", "Failed to start download. Please try again.")
      );
    }
  };

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    if (!jobId) return;

    try {
      // Fetch question errors for the specific file using the new endpoint
      const errors = await fetchFileQuestionErrors(jobId, fileId);

      // Convert to CSV format
      const csvContent = convertToCSV(errors as unknown as Record<string, unknown>[], ['question_id', 'status', 'message']);

      // Generate filename with job ID, file name, and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const cleanFileName = fileName.replace(/\.[^/.]+$/, ''); // Remove file extension
      const filename = `job-${jobId}-file-${cleanFileName}-errors-${timestamp}.csv`;

      // Download CSV file
      downloadCSV(csvContent, filename);

      // Note: No message shown when no errors - download proceeds silently
    } catch (error: any) {
      // Handle different error types
      let errorMessage = getResourceValue(
        "download_file_errors_failed",
        "Failed to download file errors. Please try again."
      );

      if (error?.response?.status === 404) {
        const errorData = error.response?.data;
        if (errorData?.error_code === 40401) {
          errorMessage = getResourceValue(
            "job_not_found",
            "Job not found"
          );
        } else if (errorData?.error_code === 40412) {
          errorMessage = getResourceValue(
            "file_not_found",
            "File not found in job"
          );
        }
      } else if (error?.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.error_code === 40009) {
          errorMessage = getResourceValue(
            "invalid_job_id",
            "Invalid job ID format"
          );
        } else if (errorData?.error_code === 40029) {
          errorMessage = getResourceValue(
            "invalid_file_id",
            "Invalid file ID format"
          );
        } else {
          errorMessage = errorData?.message || errorMessage;
        }
      } else if (error?.response?.status === 500) {
        errorMessage = getResourceValue(
          "server_error",
          "Server error occurred. Please try again later."
        );
      }

      showError(
        getResourceValue("error", "Error"),
        errorMessage
      );
      console.error("Error downloading file errors:", error);
    }
  };

  return (
    <div className="page job-details">
      <div className="job-details__container">
        <JobDetailsHeader jobId={job.job_id} jobType={job.job_type} onDownloadZip={handleDownloadAllZip} />

        <JobDetailsInfo
          userEmail={job.user_email || getResourceValue("not_available", "N/A")}
          fileName={job.file_name}
          uploadDate={formatDateTime(job.job_created_at_utc)}
          subject={job.subject_title || getResourceValue("not_available", "N/A")}
          questionsNumber={questions.length}
          requiresGraphics={
            questions.some((q) => q.has_graphics)
              ? getResourceValue("yes", "Yes")
              : getResourceValue("no", "No")
          }
          publishedQuestions={publishedCount}
          statusCards={statusCards}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onDownloadLog={handleDownloadLog}
          onDownloadZip={handleDownloadZip}
        />

        {/* <StatusCardsGrid
          cards={statusCards}
          onPreview={(type: string) => console.log("Preview triggered for", type)}
          onDownload={handleDownload}
          onDownloadLog={handleDownloadLog}
          onDownloadZip={handleDownloadZip}
        /> */}

        <JobQuestionsTable
          questions={mappedQuestions}
          onPreview={(questionId: string) => {
            // Navigate to preview page for single question with jobId as query parameter
            navigate(`/preview-questions/results/${questionId}?jobId=${jobId}`);
          }}
          onDelete={handleDeleteClick}
        />

        {job.files && job.files.length > 0 && (
          <JobHistory
            files={job.files}
            onDownloadFile={handleDownloadFile}
          />
        )}
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setQuestionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={getResourceValue("delete_question", "Delete Question")}
        message={getResourceValue(
          "confirm_delete_question",
          "Are you sure you want to delete this question? This action cannot be undone."
        )}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default JobDetails;
